import StyledCard, { PlaceholderWrapper } from "components/UI/StyledCard";
import AddIcon from '@mui/icons-material/Add'
import Stack from "@mui/material/Stack";
import SubsectionTitle from "components/UI/SubsectionTitle.jsx/SubsectionTitle";
import { buildStaticClientField, getClientIdsFromLandingPage } from "components/pages/LandingPages/data/helpers";
import { useSuspenseQuery } from "@apollo/client";
import { GET_CONTRACTS_QUERY } from "components/pages/LandingPages/data/queries";
import { useCallback, useMemo } from "react";
import { getIn, useFormikContext } from "formik";
import MenuItem from "@mui/material/MenuItem";
import { LandingPage } from "generated/graphql";
import { filter } from "lodash-es";
import Box from "@mui/material/Box";
import { RemoveButton } from "./CampaignSelectionRules";
import EmptyStateSection from "./EmptyStateSection";
import Suspenseful from "components/Suspenseful";
import { useParams } from "react-router-dom";
import { FormSelect, FormTextField } from "components/Formik/forms";


function FieldSelector({fieldCollection, disabled, path}) {
  return (
    <FormSelect name={path} label="Field" disabled={disabled} fullWidth>
      {fieldCollection.map(field => (
        <MenuItem value={field} key={`field_${field}`}>{field}</MenuItem>
      ))}
    </FormSelect>
  )
}

function ClientSelector({clientCollection, path}) {
  return (
    <FormSelect name={path} label="Client" fullWidth>
      {clientCollection.map(client => (
        <MenuItem value={client.id} key={`client_${client.id}`}>{client.value}</MenuItem>
      ))}
    </FormSelect>
  )
}

function StaticField({clientFieldGroups, index, clientCollection}) {
  const { values, setFieldValue } = useFormikContext<LandingPage>()
  const path = `staticFields[${index}]`
  const selectedClientId = getIn(values, `${path}.clientId`)
  const fieldCollection = clientFieldGroups.find(contract => contract.client.id === selectedClientId)?.client.fields.map(field => field.name) || []

  const handleDelete = useCallback(() => {
    setFieldValue('staticFields', filter(values.staticFields, (_, i) => i !== index))
  }, [index, setFieldValue, values.staticFields])

  return (
    <Box
      sx={{
        position: 'relative',
        '&:not(:hover) .visibleOnlyOnHover': {visibility: 'hidden'},
      }}
      aria-label={path}
    >
      <Stack direction='row' gap={2}>
        <ClientSelector clientCollection={clientCollection} path={`${path}.clientId`} />
        {/* Use `key` to force remount when `selectedClientId` changes. */}
        {/* Ensures FormSelect correctly updates its disabled state, even if its collection is initially empty */}
        <FieldSelector fieldCollection={fieldCollection} disabled={!selectedClientId} path={`${path}.field`} key={selectedClientId || 'none'} />
        <FormTextField name={`${path}.value`} label="Value" fullWidth />
      </Stack>

      <RemoveButton className='visibleOnlyOnHover' onClick={handleDelete} />
    </Box>
  )
}

function StaticClientFields() {
  const { values, setFieldValue } = useFormikContext<LandingPage>()
  const { vendorId } = useParams()
  const clientIdList = getClientIdsFromLandingPage(values)
  const { data: clientData } = useSuspenseQuery(GET_CONTRACTS_QUERY, { variables: { clientIds: clientIdList, vendorId: vendorId ?? '' }, fetchPolicy: 'network-only'})
  const clientFieldGroups = useMemo(() => clientData.contracts, [clientData.contracts])
  const staticFields = useMemo(() => values.staticFields || [], [values.staticFields])
  const clientCollection = clientFieldGroups.map(contract => ({id: contract.client.id, value: contract.client.internalName || contract.client.name}))

  const handleAddStaticClientField = useCallback(() => {
    setFieldValue('staticFields', [...staticFields, buildStaticClientField()])
  }, [setFieldValue, staticFields])

  return (
    <StyledCard
      title='Static Client Fields'
      note='For landing page clients with static answers, add a field and associated value.'
      isSubsection
      hasStickyHeader
      role="group"
    >
      <PlaceholderWrapper>
        <Stack direction='column' spacing={2}>
          <SubsectionTitle
            title="Client Fields"
            controls={[
              { icon:  <AddIcon/>, label: 'Add Static Client Field', onClick: handleAddStaticClientField },
            ]}
          />
          {staticFields.map((_staticField, index) =>
            <StaticField clientFieldGroups={clientFieldGroups} index={index} key={index} clientCollection={clientCollection} />
          )}
          {staticFields.length === 0 &&
            <EmptyStateSection message="No static client fields." />
          }
        </Stack>
      </PlaceholderWrapper>
    </StyledCard>
  )
}

export default function StaticClientFieldsWrapper() {
  return (
    <Suspenseful component={StaticClientFields} />
  )
}
